import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Ref, useEffect, useImperativeHandle, useState } from 'react';
import { VariantProps } from 'tailwind-variants';

import CloseIcon from '@/fo/icons/base/icons/cross32.svg';
import { UiKitButtonBase, UiKitButtonBaseProps } from '@/fo/ui/kit/button/base';
import { UiKitDrawerBase, DialogContext } from '@/fo/ui/kit/drawer/base';
import { useMediaQuery } from '@/fo/ui/mod/media/query';

import { styles } from './UiKitDialogBase.styles';

type Variants = VariantProps<typeof styles>;

export interface UiKitDialogBaseRef {
  close: () => void;
}

export interface UiKitDialogBaseProps {
  children?: React.ReactNode;
  trigger?: React.ReactNode;
  /**
   * Заголовок диалога, требует Radix для screen reader
   */
  title: string;
  className?: string;
  drawerClassName?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  vertical?: Variants['vertical'];
  horizontal?: Variants['horizontal'];
  /**
   * Ссылка на DOM-элемент контента диалога
   */
  ref?: Ref<HTMLDivElement>;
  fullScreen?: boolean;
  /**
   * При нажатии за пределами окна диалога, окно будет закрываться
   * @default true
   */
  dismissible?: boolean;
  /**
   * Окно не будет иметь backdrop и не блокирует интерфейс вне окна диалога
   * @default true
   */
  modal?: boolean;
  colorCross?: UiKitButtonBaseProps['color'];
  /**
   * Ссылка на элемент управления диалогом
   */
  dialogRef?: Ref<UiKitDialogBaseRef>;
  customCloseButton?: React.ReactNode;
  buttonClassName?: string;
  /**
   * Автоматически фокусируется на первом элементе ввода диалога
   * @default true
   */
  autoFocus?: boolean;
}

export function UiKitDialogBase({
  children,
  trigger,
  title,
  className,
  drawerClassName,
  onOpenChange,
  vertical = 'center',
  horizontal = 'center',
  fullScreen = false,
  dismissible = true,
  modal = true,
  ref,
  colorCross = 'ghost',
  dialogRef,
  customCloseButton,
  autoFocus = true,
  buttonClassName,
  ...props
}: UiKitDialogBaseProps) {
  const slots = styles({ vertical, horizontal, fullScreen });

  const [open, setOpen] = useState(props.open);

  const close = () => {
    setOpen(false);
  };

  useImperativeHandle(dialogRef, () => ({ close }));

  useEffect(() => {
    setOpen(props.open);
  }, [props.open]);

  const handleOpenChange = (open: boolean) => {
    setOpen(open);
    onOpenChange?.(open);
  };

  const isDesktop = useMediaQuery('sm');

  const [contentRef, setContentRef] = useState<HTMLDivElement | null>(null);

  const handleRef = (node: HTMLDivElement) => {
    setContentRef(node);

    if (ref) {
      if (typeof ref === 'function') {
        ref(node);
        return;
      }

      ref.current = node;
    }
  };

  const avoidDefaultDomBehavior = (event: Event) => {
    if (!dismissible) {
      event.preventDefault();
    }
  };

  const handleOpenAutoFocus = (event: Event) => {
    if (!autoFocus) {
      event.preventDefault();
    }
  };

  if (!isDesktop && !fullScreen) {
    return (
      <UiKitDrawerBase
        title={title}
        open={open}
        onOpenChange={handleOpenChange}
        trigger={trigger}
        className={drawerClassName}
      >
        {children}
      </UiKitDrawerBase>
    );
  }

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={handleOpenChange}
      modal={modal}
    >
      {trigger && (
        <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger>
      )}
      <DialogContext.Provider value={{ contentRef }}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className={slots.overlay()} />
          <DialogPrimitive.Content
            autoFocus={autoFocus}
            onOpenAutoFocus={handleOpenAutoFocus}
            ref={handleRef}
            className={slots.contentContainer()}
            onPointerDownOutside={avoidDefaultDomBehavior}
            onInteractOutside={avoidDefaultDomBehavior}
          >
            {/* https://github.com/radix-ui/primitives/issues/2986 */}
            <DialogPrimitive.Title asChild>
              <span className="hidden">{title}</span>
            </DialogPrimitive.Title>
            <div className={slots.content({ className })}>
              <DialogPrimitive.Close asChild>
                {customCloseButton ? (
                  customCloseButton
                ) : (
                  <UiKitButtonBase
                    color={colorCross}
                    rounded="full"
                    className={slots.closeButton({
                      className: buttonClassName
                    })}
                  >
                    <CloseIcon />
                  </UiKitButtonBase>
                )}
              </DialogPrimitive.Close>
              {children}
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogContext.Provider>
    </DialogPrimitive.Root>
  );
}
